<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useEditUser, type CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkMembershipDeletePermission } from '@ilteducation/customer-mgmt-permissions';

import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import useModal, { modals } from '../../composables/useModal';
import { useSidebarUser } from '../../composables/useSidebarUser';
import { useGotoProfile } from '../../composables/useGotoProfile';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';

import MembershipChip from './MembershipChip.vue';
import DeleteMembershipModal from './DeleteMembershipModal.vue';
import SidePanelSection from '../SidePanel/SidePanelSection.vue';
import { usePermissions } from '../../composables/usePermissions';
import { useAppEvents } from '../../events/appEvents';
const { t } = useLocale();
const { toast } = useToast();
const { organization } = useCurrentOrganization();
const { open: openManageUserGroups } = useModal(modals.MANAGE_USER_GROUPS);
const { deleteMembership, isLoading: isDeletingMembership } = useEditUser();
const { getUserGroups, isLoading: isGettingUserGroups } = useSidebarUser();
const { open: openConfirmDeleteMembershipModal, close: closeConfirmDeleteMembershipModal } = useModal(
  modals.CONFIRM_DELETE_MEMBERSHIP_FROM_USER,
);
const { gotoGroupProfile } = useGotoProfile();
const { emitAppEvent } = useAppEvents();
const { permissionContext, reporterWithReason, createGetTargetAssociation, getCallerAssociation, getGroupMemberships } =
  usePermissions();

const props = defineProps<{
  user: CustomerMgmtTypes.GetUserResponse;
  memberships: CustomerMgmtTypes.GetStudentMembershipsResponse['data'];
  isLoading: boolean;
}>();
const groupToDelete = ref<CustomerMgmtTypes.MembershipOutput | null>(null);

const onDeleteMembership = (membership: CustomerMgmtTypes.MembershipOutput) => {
  groupToDelete.value = membership;
  openConfirmDeleteMembershipModal();
};

const onRejectDeleteMembership = () => {
  groupToDelete.value = null;
  closeConfirmDeleteMembershipModal();
};

const onConfirmDeleteMembership = async () => {
  if (groupToDelete.value) {
    const success = await deleteMembership(groupToDelete.value.user.uid, groupToDelete.value.group.uid);
    if (success) {
      await getUserGroups(groupToDelete.value.user.uid, organization.value.uid, true);
      emitAppEvent({ type: 'membership:updated', organizationUid: organization.value.uid });
      groupToDelete.value = null;
      closeConfirmDeleteMembershipModal();
      toast(t('lcp.membership_removed_successfully'), 'success');
    } else {
      toast(t('lcp.membership_removed_error'), 'error');
    }
  }
};

const isMembershipDeletableByUid = ref<Record<string, ReturnType<typeof reporterWithReason>>>({});
watch(
  () => [props.memberships, props.user],
  async () => {
    props.memberships.forEach(async (membership) => {
      isMembershipDeletableByUid.value[membership.group.uid] = await checkMembershipDeletePermission(
        permissionContext.value,
        {
          ...membership,
          group: {
            ...membership.group,
            organization: membership.group.schoolUnit,
          },
        },
        getCallerAssociation,
        createGetTargetAssociation(props.user),
        getGroupMemberships,
        reporterWithReason,
      );
    });
  },
  { immediate: true },
);
</script>
<template>
  <SidePanelSection :title="t('lcp.manage_groups')" :loading="isLoading" :action="openManageUserGroups">
    <v-row v-if="isLoading">
      <v-col>
        <v-skeleton-loader type="paragraph"></v-skeleton-loader>
      </v-col>
    </v-row>
    <v-row v-else class="ga-2" no-gutters>
      <v-col class="d-flex flex-wrap ga-2">
        <MembershipChip
          v-for="membership in memberships"
          :key="membership.group.uid"
          :group-name="membership.group.name"
          :years-in-school="membership.group.yearsInSchool"
          :is-deletable-or-error-message="
            isMembershipDeletableByUid[membership.group.uid]?.allowed
              ? false
              : isMembershipDeletableByUid[membership.group.uid]?.reason
          "
          @delete="() => onDeleteMembership(membership)"
          @goto-group="gotoGroupProfile(membership.group.uid)"
        >
        </MembershipChip>
      </v-col>
    </v-row>
  </SidePanelSection>
  <DeleteMembershipModal
    :name="modals.CONFIRM_DELETE_MEMBERSHIP_FROM_USER"
    :onConfirm="onConfirmDeleteMembership"
    :onReject="onRejectDeleteMembership"
    :is-loading="isDeletingMembership || isGettingUserGroups"
  />
</template>

<style lang="scss" scoped></style>
